/**
 * WebSocket 聊天客户端 —— 替代 tim.js + newTim.js
 *
 * 接口与原有 tim.js / newTim.js 完全兼容，业务页面只需改 import 路径。
 * 原有 TIM SDK 事件名和常量名均通过 TIM_TYPES / TIM_EVENT 兼容导出。
 *
 * 使用前需在登录后调用 login(userId, userSig) / connect(userId, token)
 * 其中 userSig 参数在 WebSocket 模式下传 JWT token 即可。
 *
 * 连接地址: ws://{host}/ws/chat?token={token}&userType={userType}
 */
import { Message } from 'element-ui';
import { getToken } from '@/utils/auth';

// ==================== 兼容常量（对齐 tim-js-sdk） ====================
export const TIM_TYPES = {
    CONV_C2C: 'C2C',
    CONV_GROUP: 'GROUP',
    MSG_PRIORITY_HIGH: 'high',
    MSG_PRIORITY_NORMAL: 'normal',
    MSG_PRIORITY_LOW: 'low',
    MSG_PRIORITY_LOWEST: 'lowest',
    KICKED_OUT_MULT_ACCOUNT: 'multipleAccount',
    KICKED_OUT_MULT_DEVICE: 'multipleDevice',
    KICKED_OUT_USERSIG_EXPIRED: 'userSigExpired',
    NET_STATE_CONNECTED: 'connected',
    NET_STATE_CONNECTING: 'connecting',
    NET_STATE_DISCONNECTED: 'disconnected',
    SNS_APPLICATION_SENT_TO_ME: 'sentToMe',
    SNS_APPLICATION_SENT_BY_ME: 'sentByMe'
};

export const TIM_EVENT = {
    SDK_READY: 'onSDKReady',
    MESSAGE_RECEIVED: 'onMessageReceived',
    MESSAGE_MODIFIED: 'onMessageModified',
    MESSAGE_REVOKED: 'onMessageRevoked',
    MESSAGE_READ_BY_PEER: 'onMessageReadByPeer',
    CONVERSATION_LIST_UPDATED: 'onConversationListUpdated',
    GROUP_LIST_UPDATED: 'onGroupListUpdated',
    PROFILE_UPDATED: 'onProfileUpdated',
    BLACKLIST_UPDATED: 'onBlacklistUpdated',
    ERROR: 'onError',
    SDK_NOT_READY: 'onSDKNotReady',
    KICKED_OUT: 'onKickedOut',
    NET_STATE_CHANGE: 'onNetStateChange',
    FRIEND_LIST_UPDATED: 'onFriendListUpdated',
    FRIEND_APPLICATION_LIST_UPDATED: 'onFriendApplicationListUpdated',
    FRIEND_GROUP_LIST_UPDATED: 'onFriendGroupListUpdated'
};

// ==================== 内部状态 ====================
const WS_BASE_URL = process.env.VUE_APP_WS_URL || (window.location.origin.replace(/^http/, 'ws') + '/baiyi');
const RECONNECT_MAX = 10;
const HEARTBEAT_INTERVAL = 25000; // 25秒心跳
const RECONNECT_BASE_DELAY = 1000;

let ws = null;                     // WebSocket 实例
let isConnected = false;           // 连接状态
let connectingPromise = null;      // 连接中 Promise，避免重复创建连接
let reconnectCount = 0;            // 重连计数
let heartbeatTimer = null;         // 心跳定时器
let messageQueue = [];             // 离线消息队列（连接后批量发送）
let conversationListCache = null;   // 会话列表短缓存，避免列表页逐项重复查
let conversationListCacheAt = 0;
let conversationListPromise = null;

// 事件回调注册表
let eventCallbacks = {
    [TIM_EVENT.SDK_READY]: [],
    [TIM_EVENT.MESSAGE_RECEIVED]: [],
    [TIM_EVENT.MESSAGE_MODIFIED]: [],
    [TIM_EVENT.MESSAGE_REVOKED]: [],
    [TIM_EVENT.MESSAGE_READ_BY_PEER]: [],
    [TIM_EVENT.CONVERSATION_LIST_UPDATED]: [],
    [TIM_EVENT.ERROR]: [],
    [TIM_EVENT.SDK_NOT_READY]: [],
    [TIM_EVENT.KICKED_OUT]: [],
    [TIM_EVENT.NET_STATE_CHANGE]: []
};

let currentUserId = null;
let currentUserType = 2; // 默认医生

function getBusinessUserId() {
    return currentUserId || localStorage.getItem('public-doctor-userId') || '';
}

function getBusinessReceiverId(toUserId) {
    return localStorage.getItem('current-chat-patientId') || toUserId || '';
}

/**
 * 前端统一消息类型（与业务约定一致，禁止再映射成 TEXT/CUSTOM 混淆）：
 * TIMTextElem | TIMImageElem | TIMCustomElem |
 * TIMDoctorCardElem | TIMPatientCardElem | TIMPrescriptionCardElem
 */
const TIM_TYPES_SET = {
    TIMTextElem: true,
    TIMImageElem: true,
    TIMCustomElem: true,
    TIMDoctorCardElem: true,
    TIMPatientCardElem: true,
    TIMPrescriptionCardElem: true
};
const CARD_TYPES = ['TIMDoctorCardElem', 'TIMPatientCardElem', 'TIMPrescriptionCardElem'];
const TIM_TYPE_PRIORITY = [
    'TIMDoctorCardElem',
    'TIMPatientCardElem',
    'TIMPrescriptionCardElem',
    'TIMCustomElem',
    'TIMImageElem',
    'TIMTextElem'
];

function isCardType(t) {
    return CARD_TYPES.indexOf(t) > -1;
}

function isTimType(t) {
    return !!(t && TIM_TYPES_SET[t]);
}

/** 尝试把字符串解析成对象 */
function tryParseJson(value) {
    if (value == null || value === '') return null;
    if (typeof value === 'object') return value;
    if (typeof value !== 'string') return null;
    const s = value.trim();
    if (!s) return null;
    if (isTimType(s)) return { type: s };
    try {
        let data = JSON.parse(s);
        if (typeof data === 'string') {
            try { data = JSON.parse(data); } catch (e) { /* keep */ }
        }
        return typeof data === 'object' && data ? data : null;
    } catch (e) {
        return null;
    }
}

/** 任意原始值 → 标准 TIM 类型；无法识别返回空串 */
function toTimType(raw) {
    if (raw == null || raw === '') return '';
    const s = String(raw).trim();
    if (isTimType(s)) return s;
    const upper = s.toUpperCase();
    // 兼容旧线协议别名，最终仍落到 TIM*
    if (upper === 'TEXT' || s === '0') return 'TIMTextElem';
    if (upper === 'IMAGE' || s === '1') return 'TIMImageElem';
    if (upper === 'CUSTOM' || s === '2' || s === '3') return 'TIMCustomElem';
    return '';
}

/** 从 content JSON 取业务类型 */
function resolveCustomMessageType(content) {
    if (typeof content === 'string' && isTimType(content.trim())) {
        return content.trim();
    }
    const data = tryParseJson(content);
    const t = data && (data.type || data.cardType || data.businessID);
    if (isTimType(t) && t !== 'TIMTextElem' && t !== 'TIMImageElem') return t;
    return '';
}

/** 从历史/WS 原始项取出 content */
function pickRawContent(item) {
    if (!item) return '';
    if (item.content != null && item.content !== '') return item.content;
    if (item.msg != null && item.msg !== '') return item.msg;
    if (item.message != null && item.message !== '') return item.message;
    if (item.payload != null) {
        if (typeof item.payload === 'string') return item.payload;
        if (item.payload.data != null) return item.payload.data;
        if (item.payload.text != null) return item.payload.text;
    }
    return '';
}

/**
 * 解析消息标准 TIM 类型。
 * 优先认 msgType/type/description 上的 TIMPatientCardElem 等，
 * 绝不能被同条里的 TEXT 别名盖掉。
 */
function resolveTimMessageType(item) {
    const candidates = [];
    const push = (raw) => {
        const t = toTimType(raw);
        if (t && candidates.indexOf(t) < 0) candidates.push(t);
    };
    // 字段顺序：业务类型字段优先于可能被误标的 msgType
    push(item && item.description);
    push(item && item.extension);
    push(item && item.msgDesc);
    push(item && item.msgType);
    push(item && item.type);
    push(item && item.messageType);

    const content = pickRawContent(item);
    const fromContent = resolveCustomMessageType(content);
    if (fromContent && candidates.indexOf(fromContent) < 0) {
        candidates.push(fromContent);
    }

    for (let i = 0; i < TIM_TYPE_PRIORITY.length; i++) {
        if (candidates.indexOf(TIM_TYPE_PRIORITY[i]) > -1) {
            return TIM_TYPE_PRIORITY[i];
        }
    }
    return 'TIMTextElem';
}

/** content 字符串化 */
function contentToString(raw) {
    if (raw == null) return '';
    if (typeof raw === 'object') return JSON.stringify(raw);
    return String(raw);
}

/**
 * 历史/实时统一：得到 { type: TIM*, content }
 * type 始终是 6 种之一，不再产出 TEXT/IMAGE/CUSTOM
 */
function resolveChatItemType(item) {
    const content = contentToString(pickRawContent(item));
    const type = resolveTimMessageType(item);
    return { type, content };
}

/** 等待 WS 就绪 */
function waitForWsReady(timeoutMs = 10000) {
    if (ws && ws.readyState === WebSocket.OPEN) {
        return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
        const started = Date.now();
        const check = () => {
            if (ws && ws.readyState === WebSocket.OPEN) {
                resolve();
                return;
            }
            if (Date.now() - started >= timeoutMs) {
                reject(new Error('WebSocket 连接超时'));
                return;
            }
            setTimeout(check, 80);
        };
        connect().then(check).catch(() => check());
    });
}

// ==================== 核心连接 ====================
function getWsUrl() {
    const token = getToken();
    const userId = getBusinessUserId();
    return `${WS_BASE_URL}/ws/chat?token=${token}&userType=${currentUserType}&userId=${userId}`;
}

function connect() {
    let url;
    try {
        url = getWsUrl();
        console.log('[WS][connect] 准备连接', {
            url,
            currentUserId,
            businessUserId: getBusinessUserId(),
            publicDoctorUserId: localStorage.getItem('public-doctor-userId'),
            currentUserType
        });
    } catch (e) {
        console.error('[WS] URL构造异常:', e);
        return Promise.reject(e);
    }
    if (ws && ws.readyState === WebSocket.OPEN) {
        return Promise.resolve();
    }
    if (connectingPromise) {
        return connectingPromise;
    }

    connectingPromise = new Promise((resolve, reject) => {
        try {
            const socket = new WebSocket(url);
            ws = socket;

            socket.onopen = () => {
                if (ws !== socket) return;
                console.log('[WS][connect] 已连接', {
                    businessUserId: getBusinessUserId(),
                    publicDoctorUserId: localStorage.getItem('public-doctor-userId'),
                    currentUserType
                });
                isConnected = true;
                connectingPromise = null;
                reconnectCount = 0;
                startHeartbeat();
                flushQueue();
                fireEvent(TIM_EVENT.SDK_READY, { name: TIM_EVENT.SDK_READY });
                fireEvent(TIM_EVENT.NET_STATE_CHANGE, {
                    name: TIM_EVENT.NET_STATE_CHANGE,
                    data: { state: TIM_TYPES.NET_STATE_CONNECTED }
                });
                resolve();
            };

            socket.onerror = (error) => {
                if (ws !== socket) return;
                console.error('[WS] 连接错误', error);
                isConnected = false;
                connectingPromise = null;
                fireEvent(TIM_EVENT.ERROR, {
                    name: TIM_EVENT.ERROR,
                    data: { code: -1, message: 'WebSocket 连接错误' }
                });
                reject(error);
            };

            socket.onmessage = (event) => {
                try {
                    const msg = JSON.parse(event.data);
                    handleServerMessage(msg);
                } catch (e) {
                    console.error('[WS] 消息解析失败', e);
                }
            };

            socket.onclose = (event) => {
                if (ws !== socket) return;
                isConnected = false;
                connectingPromise = null;
                stopHeartbeat();
                fireEvent(TIM_EVENT.NET_STATE_CHANGE, {
                    name: TIM_EVENT.NET_STATE_CHANGE,
                    data: { state: TIM_TYPES.NET_STATE_DISCONNECTED }
                });
                if (event && (event.code === 1000 || event.code === 1001)) {
                    return;
                }
                tryReconnect();
            };

        } catch (e) {
            console.error('[WS] WebSocket创建异常:', e);
            connectingPromise = null;
            reject(e);
        }
    });
    return connectingPromise;
}

function disconnect() {
    stopHeartbeat();
    if (ws) {
        ws.close(1000, '主动断开');
        ws = null;
    }
    isConnected = false;
    connectingPromise = null;
    reconnectCount = RECONNECT_MAX; // 阻止重连
}

function tryReconnect() {
    if (reconnectCount >= RECONNECT_MAX) return;
    const delay = RECONNECT_BASE_DELAY * Math.pow(2, reconnectCount);
    reconnectCount++;
    console.log(`[WS] ${delay}ms 后尝试第${reconnectCount}次重连`);
    fireEvent(TIM_EVENT.NET_STATE_CHANGE, {
        name: TIM_EVENT.NET_STATE_CHANGE,
        data: { state: TIM_TYPES.NET_STATE_CONNECTING }
    });
    setTimeout(() => connect().catch(() => {}), delay);
}

// ==================== 心跳 ====================
function startHeartbeat() {
    stopHeartbeat();
    heartbeatTimer = setInterval(() => {
        if (ws && ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'HEARTBEAT' }));
        }
    }, HEARTBEAT_INTERVAL);
}

function stopHeartbeat() {
    if (heartbeatTimer) {
        clearInterval(heartbeatTimer);
        heartbeatTimer = null;
    }
}

function flushQueue() {
    while (messageQueue.length > 0) {
        const msg = messageQueue.shift();
        if (ws && ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify(msg));
        }
    }
}

// ==================== 事件系统（兼容 TIM SDK event 机制） ====================
function on(eventName, callback) {
    if (eventCallbacks[eventName]) {
        eventCallbacks[eventName].push(callback);
    }
}

function off(eventName, callback) {
    if (eventCallbacks[eventName]) {
        eventCallbacks[eventName] = eventCallbacks[eventName].filter(cb => cb !== callback);
    }
}

function fireEvent(eventName, data) {
    (eventCallbacks[eventName] || []).forEach(cb => {
        try { cb(data); } catch (e) { console.error('[WS] 回调异常', e); }
    });
}

// ==================== 消息处理 ====================

// 待确认的ACK回调 { clientMsgId -> { success, error } }
const pendingAcks = {};

function handleServerMessage(msg) {
    switch (msg.type) {
        case 'ACK':
            console.log('[WS] 收到ACK:', msg);
            // 消息已送达服务端，触发发送者的成功回调
            // ACK 消息: { type: "ACK", clientMsgId, messageId, timestamp }
            if (msg.clientMsgId && pendingAcks[msg.clientMsgId]) {
                const { success, toUserId } = pendingAcks[msg.clientMsgId];
                delete pendingAcks[msg.clientMsgId];
                // 构造与 sendMessage 回调期望一致的消息对象
                if (success) {
                    success({
                        code: 0,
                        data: {
                            message: {
                                ID: msg.messageId || msg.clientMsgId,
                                type: 'TIMTextElem',
                                conversationID: 'C2C' + (toUserId || ''),
                                flow: 'out',
                                time: msg.timestamp || Math.floor(Date.now() / 1000),
                                payload: { text: msg.content || '' }
                            }
                        }
                    });
                }
            }
            break;
        case 'CHAT':
            conversationListCacheAt = 0;
            console.log('[WS] 收到CHAT消息, fromUserId:', msg.fromUserId, 'toUserId:', msg.toUserId, 'flow:', msg.flow, 'content:', msg.content);
            // 收到新消息 —— 包装成 TIM SDK 兼容格式
            const wrappedMsg = wrapMessage(msg);
            fireEvent(TIM_EVENT.MESSAGE_RECEIVED, {
                name: 'onMessageReceived',
                data: [wrappedMsg]
            });
            break;
        case 'HEARTBEAT':
            break;
        case 'ERROR':
            console.error('[WS] 服务端错误:', msg.content);
            fireEvent(TIM_EVENT.ERROR, {
                name: TIM_EVENT.ERROR,
                data: { code: -1, message: msg.content }
            });
            break;
    }
}

/** 统一为秒级时间戳，避免历史(毫秒)与本地发送(秒)排序错乱 */
function normalizeMsgTime(t) {
    if (t == null || t === '') return Math.floor(Date.now() / 1000);
    if (typeof t === 'string' && /^\d+$/.test(t.trim())) {
        t = Number(t.trim());
    }
    if (typeof t === 'string') {
        const parsed = Date.parse(String(t).replace(/-/g, '/'));
        if (!isNaN(parsed)) return Math.floor(parsed / 1000);
        return Math.floor(Date.now() / 1000);
    }
    const n = Number(t);
    if (!n || isNaN(n)) return Math.floor(Date.now() / 1000);
    // 毫秒时间戳
    if (n > 1e12) return Math.floor(n / 1000);
    return Math.floor(n);
}

/**
 * 将 WebSocket 消息包装成 TIM SDK Message 兼容格式
 */
function wrapMessage(msg) {
    const now = Math.floor(Date.now() / 1000);
    const fromUserId = msg.fromUserId || msg.from;
    const toUserId = msg.toUserId || msg.to;
    const resolved = resolveChatItemType(msg);
    return {
        ID: msg.messageId,
        type: resolved.type,
        conversationID: 'C2C' + (msg.flow === 'in' ? fromUserId : toUserId),
        conversationType: 'C2C',
        flow: msg.flow,
        fromUserId: fromUserId,
        toUserId: toUserId,
        from: fromUserId,
        to: toUserId,
        time: normalizeMsgTime(msg.timestamp != null ? msg.timestamp : (msg.time != null ? msg.time : now)),
        isPeerRead: false,
        payload: buildPayload(resolved.type, resolved.content),
        clientMsgId: msg.clientMsgId,
        avatar: '',
        nick: msg.flow === 'in' ? msg.fromUserName : msg.toUserName,
        rxId: msg.rxId
    };
}

/** 按标准 TIM type 组装 payload */
function buildPayload(timType, content) {
    if (timType === 'TIMImageElem') {
        return {
            imageInfoArray: [
                {},
                { url: resolveImageUrl(content), imageUrl: resolveImageUrl(content), width: 200, height: 200 },
                {}
            ]
        };
    }
    if (
        timType === 'TIMCustomElem' ||
        timType === 'TIMDoctorCardElem' ||
        timType === 'TIMPatientCardElem' ||
        timType === 'TIMPrescriptionCardElem'
    ) {
        return {
            data: content,
            description: timType,
            extension: timType
        };
    }
    return { text: content };
}

function resolveImageUrl(fileId) {
    if (!fileId) return '';
    if (/^https?:\/\//.test(fileId) || /^blob:/.test(fileId)) {
        return fileId;
    }
    return `${process.env.VUE_APP_DOWNLOAD_SERVER || ''}${fileId}`;
}

// ==================== 消息发送（兼容 tim.js / newTim.js 接口） ====================

/** 发送文本/图片消息（兼容 tim.js sendMessage） */
function sendMessage(type, to, payload, success, error) {
    if (!ws || ws.readyState !== WebSocket.OPEN) {
        if (error) error({ code: -1, message: 'WebSocket 未连接' });
        return;
    }

    let msgType, content;
    if (type === 1) {
        msgType = 'TIMTextElem';
        content = payload.text || payload;
    } else if (type === 2) {
        msgType = 'TIMImageElem';
        content = typeof payload === 'string' ? payload : (payload.file || '');
    } else {
        if (error) error({ code: -1, message: '不支持的消息类型' });
        return;
    }

    const clientMsgId = generateId();
    const rxIdForMsg = localStorage.getItem('current-rxId') || '';
    const wsMsg = {
        type: 'CHAT',
        conversationType: 'C2C',
        msgType: msgType,
        toUserId: to,
        toUserType: currentUserType === 1 ? 2 : 1,
        sender: getBusinessUserId(),
        senderType: currentUserType,
        receiver: getBusinessReceiverId(to),
        receiverType: currentUserType === 1 ? 2 : 1,
        content: content,
        rxId: rxIdForMsg,
        clientMsgId: clientMsgId
    };

    console.log('[WS][sendMessage] 发送payload', wsMsg);
    ws.send(JSON.stringify(wsMsg));

    if (success) {
        success({
            code: 0,
            data: {
                message: {
                    ID: clientMsgId,
                    type: msgType,
                    conversationID: 'C2C' + to,
                    flow: 'out',
                    time: Math.floor(Date.now() / 1000),
                    payload: buildPayload(msgType, content)
                }
            }
        });
    }
}

/**
 * 发送文本消息（兼容 newTim.js sendTextMessage）
 * 医生端发送约定：toUserId=患者createUser；sender/receiver=医生ID
 */
async function sendTextMessage(toUserID, text, rxId) {
    if (!toUserID) {
        return { code: -1, msg: 'toUserId为空' };
    }
    const peerId = String(toUserID);
    const doctorId = String(getBusinessUserId() || '');
    const clientMsgId = generateId();
    const conversationID = 'C2C' + peerId;
    const now = Date.now() / 1000;
    const rxIdForMsg = rxId || localStorage.getItem('current-rxId') || '';

    const wsMsg = {
        type: 'CHAT',
        conversationType: 'C2C',
        msgType: 'TIMTextElem',
        toUserId: peerId,
        toUserType: 1,
        sender: doctorId,
        senderType: 2,
        receiver: doctorId,
        receiverType: 2,
        content: text,
        rxId: rxIdForMsg,
        clientMsgId: clientMsgId
    };

    console.log('[WS][sendTextMessage] 准备发送', JSON.stringify(wsMsg));
    try {
        await waitForWsReady();
        ws.send(JSON.stringify(wsMsg));
    } catch (e) {
        console.error('[WS][sendTextMessage] 失败', e);
        return { code: -1, msg: (e && e.message) || '发送失败' };
    }
    return {
        code: 0,
        data: {
            message: {
                ID: clientMsgId,
                clientMsgId: clientMsgId,
                type: 'TIMTextElem',
                payload: { text },
                conversationID: conversationID,
                flow: 'out',
                time: now
            }
        }
    };
}

/** 发送图片消息（兼容 newTim.js sendImageMessage） */
async function sendImageMessage(toUserID, files, protocolMsgType = 'TIMImageElem') {
    const clientMsgId = generateId();
    const conversationID = 'C2C' + toUserID;
    const now = Date.now() / 1000;
    const rxIdForMsg = localStorage.getItem('current-rxId') || '';

    const filePath = typeof files === 'string' ? files
        : (files.tempFilePaths ? files.tempFilePaths[0] : files.file || '');

    const wsMsg = {
        type: 'CHAT',
        conversationType: 'C2C',
        msgType: protocolMsgType,
        toUserId: toUserID,
        toUserType: currentUserType === 1 ? 2 : 1,
        sender: getBusinessUserId(),
        senderType: currentUserType,
        receiver: getBusinessReceiverId(toUserID),
        receiverType: currentUserType === 1 ? 2 : 1,
        content: filePath,
        rxId: rxIdForMsg,
        clientMsgId: clientMsgId
    };

    console.log('[WS][sendImageMessage] 发送payload', JSON.stringify(wsMsg));
    return new Promise((resolve, reject) => {
        if (!ws || ws.readyState !== WebSocket.OPEN) {
            reject(new Error('WebSocket 未连接'));
            return;
        }
        ws.send(JSON.stringify(wsMsg));
        resolve({
            code: 0,
            data: {
                message: {
                    ID: clientMsgId,
                    clientMsgId: clientMsgId,
                    type: 'TIMImageElem',
                    payload: { imageInfoArray: [{}, { url: resolveImageUrl(filePath), imageUrl: resolveImageUrl(filePath) }, {}] },
                    conversationID: conversationID,
                    flow: 'out',
                    time: now
                }
            }
        });
    });
}

/**
 * 发送自定义/卡片消息
 * msgType 直接使用 TIMDoctorCardElem / TIMPatientCardElem / TIMPrescriptionCardElem / TIMCustomElem
 * 医生端：toUserId=患者createUser；sender/receiver=医生ID
 */
async function sendCustomMessage(toUserID, data) {
    if (!toUserID) {
        return { code: -1, msg: 'toUserId为空' };
    }
    const peerId = String(toUserID);
    const doctorId = String(getBusinessUserId() || '');
    const clientMsgId = generateId();
    const conversationID = 'C2C' + peerId;
    const now = Date.now() / 1000;
    const rxIdForMsg = localStorage.getItem('current-rxId') || '';
    const payloadObj = typeof data === 'string' ? (tryParseJson(data) || {}) : (data || {});
    const cardType = toTimType(payloadObj.type || payloadObj.cardType)
        || resolveCustomMessageType(payloadObj)
        || 'TIMCustomElem';
    // content 内也带上 type，历史解析可双保险
    if (!payloadObj.type) payloadObj.type = cardType;
    const payload = JSON.stringify(payloadObj);

    const wsMsg = {
        type: 'CHAT',
        conversationType: 'C2C',
        msgType: cardType,
        toUserId: peerId,
        toUserType: 1,
        sender: doctorId,
        senderType: 2,
        receiver: doctorId,
        receiverType: 2,
        content: payload,
        rxId: rxIdForMsg,
        clientMsgId: clientMsgId,
        description: cardType,
        extension: cardType
    };

    console.log('[WS][sendCustomMessage] 准备发送', {
        toUserId: wsMsg.toUserId,
        sender: wsMsg.sender,
        receiver: wsMsg.receiver,
        cardType,
        content: payload
    });
    try {
        await waitForWsReady();
        ws.send(JSON.stringify(wsMsg));
    } catch (e) {
        console.error('[WS][sendCustomMessage] 失败', e);
        return { code: -1, msg: (e && e.message) || '发送失败' };
    }
    return {
        code: 0,
        data: {
            message: {
                ID: clientMsgId,
                type: cardType,
                // 本地乐观消息用对象，避免执业证等字段解析失败
                payload: { data: Object.assign({}, payloadObj), description: cardType, extension: cardType },
                conversationID: conversationID,
                flow: 'out',
                time: Math.floor(now)
            }
        }
    };
}

// ==================== 历史消息（改为调 REST API） ====================

/** 获取消息列表（兼容 tim.js getMessageList） */
async function getMessageList(param, success, error) {
    if (typeof param === 'string') {
        return getMessageListV2(param, success, error);
    }
    // param: { conversationID, nextReqMessageID, count }
    // conversationID 格式: "C2C12345" → 需要解析出 targetUserId
    const rxIdForGetMsg = localStorage.getItem('current-rxId') || '';
    const lastMsgTime = param.nextReqMessageID ? parseInt(param.nextReqMessageID) || 0 : 0;
    const pageSize = param.count || 20;

    try {
        const request = (await import('@/utils/request')).default;
        const res = await request({
            url: '/api/chat/history',
            method: 'get',
            params: { rxId: rxIdForGetMsg, pageIndex: 1, pageSize, lastMsgTime, userId: getBusinessUserId() }
        });
        if (res.code === 0) {
            const messageList = (res.data.messageList || []).map(item => wrapRESTMessage(item));
            messageList.sort((a, b) => (a.time || 0) - (b.time || 0));
            if (success) {
                success({
                    data: {
                        messageList,
                        nextReqMessageID: res.data.nextReqMessageID,
                        isCompleted: res.data.isCompleted
                    }
                });
            }
            return messageList;
        } else {
            if (error) error(res);
            if (success) success({ data: { messageList: [], nextReqMessageID: '', isCompleted: true } });
            return [];
        }
    } catch (e) {
        if (error) error(e);
        throw e;
    }
}

/** 获取消息列表（兼容 newTim.js getMessageList） */
async function getMessageListV2(conversationID, lastMsgTime, count) {
    const rxIdForList = localStorage.getItem('current-rxId') || '';
    const request = (await import('@/utils/request')).default;
    const res = await request({
        url: '/api/chat/history',
        method: 'get',
        params: {
            rxId: rxIdForList,
            pageIndex: 1,
            pageSize: count || 20,
            lastMsgTime: lastMsgTime || 0,
            userId: getBusinessUserId()
        }
    });
    if (res.code === 0) {
        const list = (res.data.messageList || []).map(item => wrapRESTMessage(item));
        // 历史接口可能新→旧，统一按时间正序（旧→新）
        list.sort((a, b) => (a.time || 0) - (b.time || 0));
        return list;
    }
    return [];
}

/** 获取会话列表（兼容 TIM SDK getConversationList） */
async function getConversationList(conversationIDList) {
    const allConversationList = await fetchConversationList();
    const targetIds = (conversationIDList || [])
        .map(item => String(item || '').replace(/^C2C/, ''))
        .filter(Boolean);

    const conversationList = targetIds.length > 0
        ? allConversationList.filter(item => targetIds.includes(String(item.userProfile.userID)))
        : allConversationList;

    return {
        data: {
            conversationList
        }
    };
}

async function fetchConversationList() {
    const now = Date.now();
    if (conversationListCache && now - conversationListCacheAt < 1500) {
        return conversationListCache;
    }
    if (conversationListPromise) {
        return conversationListPromise;
    }

    const request = (await import('@/utils/request')).default;
    conversationListPromise = request({
        url: '/api/chat/conversations',
        method: 'get',
        params: { userId: getBusinessUserId() }
    }).then(res => {
        if (res.code !== 0) {
            return [];
        }

        const conversationList = (res.data || []).map(item => ({
            conversationID: 'C2C' + item.targetUserId,
            conversationType: 'C2C',
            unreadCount: item.unreadCount || 0,
            lastMessage: {
                messageForShow: item.lastMessage,
                type: item.lastMsgType,
                lastTime: item.lastTime
            },
            userProfile: {
                userID: item.targetUserId,
                nick: item.targetUserName
            },
            rxId: item.rxId
        }));
        conversationListCache = conversationList;
        conversationListCacheAt = Date.now();
        return conversationList;
    }).finally(() => {
        conversationListPromise = null;
    });

    return conversationListPromise;
}

function wrapRESTMessage(item) {
    const resolved = resolveChatItemType(item);
    return {
        ID: item.messageId || item.id || item.clientMsgId,
        type: resolved.type,
        conversationID: 'C2C' + (item.flow === 'in' ? item.fromUserId : item.toUserId),
        conversationType: 'C2C',
        flow: item.flow,
        from: item.fromUserId,
        to: item.toUserId,
        time: normalizeMsgTime(item.timestamp != null ? item.timestamp
            : (item.time != null ? item.time : item.createTime)),
        payload: buildPayload(resolved.type, resolved.content),
        clientMsgId: item.clientMsgId,
        avatar: '',
        nick: item.flow === 'in' ? item.fromUserName : item.toUserName,
        rxId: item.rxId
    };
}

// ==================== 其他兼容接口 ====================

function watchMessage(callBack, _this) {
    if (_this && _this.__wsWatchMessageCallback) {
        off(TIM_EVENT.MESSAGE_RECEIVED, _this.__wsWatchMessageCallback);
    }
    const handler = (event) => {
        if (callBack) {
            callBack(event, _this);
        }
    };
    if (_this) {
        _this.__wsWatchMessageCallback = handler;
    }
    on(TIM_EVENT.MESSAGE_RECEIVED, handler);
}

function getTim() {
    return {
        on, off, login: connect,
        sendMessage, getMessageList,
        getConversationList,
        setMessageRead,
        createTextMessage: (opts) => ({}),
        createImageMessage: (opts) => ({}),
        createCustomMessage: (opts) => ({}),
        TYPES: TIM_TYPES,
        EVENT: TIM_EVENT
    };
}

function loginTim() {
    currentUserId = localStorage.getItem('public-doctor-userId') || currentUserId;
    return connect().catch(e => console.error('[WS] loginTim error:', e));
}

/** 登录（兼容 newTim.js login） */
function login(userID, userSig) {
    const nextUserId = userID || localStorage.getItem('public-doctor-userId') || currentUserId;
    if (nextUserId && currentUserId && String(nextUserId) !== String(currentUserId)) {
        console.log('[WS][login] 用户切换，重建连接', {
            from: currentUserId,
            to: nextUserId
        });
        disconnect();
        reconnectCount = 0;
    }
    currentUserId = nextUserId;
    // userSig 在 WebSocket 模式下为 token，但 token 已从 cookie 读取
    return connect();
}

/** 退出登录（兼容 newTim.js logout） */
async function logout() {
    disconnect();
    return true;
}

function onMessageReceived(callback) {
    on(TIM_EVENT.MESSAGE_RECEIVED, callback);
}

function onSDKReady(callback) {
    if (isConnected) {
        callback();
    } else {
        on(TIM_EVENT.SDK_READY, callback);
    }
}

async function setMessageRead(conversationID) {
    const rxIdForSetRead = localStorage.getItem('current-rxId') || '';
    const request = (await import('@/utils/request')).default;
    return await request({
        url: '/api/chat/read',
        method: 'post',
        data: { rxId: rxIdForSetRead, userId: getBusinessUserId() }
    });
}

// ==================== 工具函数 ====================
function generateId() {
    return Date.now().toString(36) + Math.random().toString(36).substring(2, 10);
}

// ==================== 导出 ====================

// 构造兼容 tim 对象（代替 tim-js-sdk 的 TIM.create() 返回值）
// 支持: tim.getMessageList(options) / tim.login({userID, userSig}) 等直接调用
const tim = {
    // 事件
    on,
    off,

    // 连接
    login: ({ userID, userSig }) => {
        const nextUserId = userID || localStorage.getItem('public-doctor-userId') || currentUserId;
        if (nextUserId && currentUserId && String(nextUserId) !== String(currentUserId)) {
            console.log('[WS][tim.login] 用户切换，重建连接', {
                from: currentUserId,
                to: nextUserId
            });
            disconnect();
            reconnectCount = 0;
        }
        currentUserId = nextUserId;
        return connect();
    },
    logout: () => disconnect(),

    // 发送（直接调用）
    sendMessage,
    sendTextMessage,
    sendImageMessage,
    sendCustomMessage,
    createTextMessage: (opts) => ({}),
    createImageMessage: (opts) => ({}),
    createCustomMessage: (opts) => ({}),

    // 历史
    getMessageList: async (options) => {
        // options 是 TIM SDK 格式: { conversationID, nextReqMessageID, count }
        const rxIdForTim = localStorage.getItem('current-rxId') || '';
        const lastMsgTime = options.nextReqMessageID ? parseInt(options.nextReqMessageID) || 0 : 0;
        const pageSize = options.count || 20;
        const request = (await import('@/utils/request')).default;
        const res = await request({
            url: '/api/chat/history',
            method: 'get',
            params: { rxId: rxIdForTim, pageIndex: 1, pageSize, lastMsgTime, userId: getBusinessUserId() }
        });
        if (res.code === 0) {
            const messageList = (res.data.messageList || []).map(wrapRESTMessage);
            return {
                data: {
                    messageList,
                    nextReqMessageID: res.data.nextReqMessageID,
                    isCompleted: res.data.isCompleted
                }
            };
        }
        return { data: { messageList: [], nextReqMessageID: null, isCompleted: true } };
    },

    getConversationList,

    // 已读
    setMessageRead,

    // 常量
    TYPES: TIM_TYPES,
    EVENT: TIM_EVENT
};

// 兼容 default import (import TIM from 'tim-js-sdk')
export default {
    create: () => ({ ...tim }),
    TYPES: TIM_TYPES,
    EVENT: TIM_EVENT
};

// tim.js 兼容导出
export {
    connect,
    disconnect,
    loginTim,
    getTim,
    sendMessage,
    getMessageList,
    getConversationList,
    watchMessage
};

// newTim.js 兼容导出（tim 对象兼容原 SDK 实例）
export {
    tim,
    login,
    logout,
    sendTextMessage,
    sendCustomMessage,
    sendImageMessage,
    onMessageReceived,
    onSDKReady,
    setMessageRead
};



// WEBPACK FOOTER //
// ./src/utils/websocket.js